import React, { Component } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import cn from 'classnames'
import FilterButton from './FilterButton'

import { EventsIndexProps } from '../EventsIndex'
import { getImageUrl } from '../utils/fileUtils'

function preprocessInput(input) {
  input = (input || '').replace(/^unit (\d)+[\s,./]+/i, '$1/')
  return input
}

const transformShortMonth = (month = '') => ({ Sep: 'Sep' }[month] || month)

const discardVenuesInGroups = (venues = [], venueGroups = []) => {
  const discardIds = venueGroups.reduce(
    (arr, venueGroup) => [...arr, ...(venueGroup.venues || []).map(({ id }) => id)],
    []
  )
  return venues.filter(({ id }) => discardIds.indexOf(id) < 0)
}

const countFilters = (filters = {}) => {
  let count = 0
  for (let key in filters) {
    if (key === 'view' || key === 'sortby') continue
    if (Array.isArray(filters[key])) {
      if (filters[key].length > 0) count++
    } else if (!!filters[key]) {
      count++
    }
  }
  return count
}

const hasFilters = (filters = {}) => countFilters(filters) >= 1

const dateIsSelectedByPeriod = (period, date) => {
  switch (period) {
    case 'this-week':
      return moment(date).isSame(new Date(), 'week')
    case 'this-weekend':
      return [
        moment()
          .day('Saturday')
          .format('YYYY-MM-DD'),
        moment()
          .day('Sunday')
          .format('YYYY-MM-DD'),
      ].includes(date)
    case 'all-weekends':
      return moment(date).day() === 0 || moment(date).day() === 6
    default:
      return false
  }
}

export default class EventFilters extends Component {
  static propTypes = {
    experiences: EventsIndexProps ? EventsIndexProps['experiences'] : PropTypes.any,
    categories: EventsIndexProps ? EventsIndexProps['categories'] : PropTypes.any,
    accessibilities: EventsIndexProps ? EventsIndexProps['accessibilities'] : PropTypes.any,
    venues: EventsIndexProps ? EventsIndexProps['venues'] : PropTypes.any,
    filters: EventsIndexProps ? EventsIndexProps['filters'] : PropTypes.any,
    days: EventsIndexProps ? EventsIndexProps['days'] : PropTypes.any,
    artists: EventsIndexProps ? EventsIndexProps['artists'] : PropTypes.any,
    toggleParamValue: PropTypes.func.isRequired,
    setParams: PropTypes.func.isRequired,
    clearParams: PropTypes.func.isRequired,
    clearAllParams: PropTypes.func.isRequired,
    numResults: PropTypes.number.isRequired,
  }

  static defaultProps = {
    filters: {},
    categories: [],
    accessibilities: [],
    venues: [],
    artists: [],
  }

  constructor(props) {
    super(props)
    this.state = { googleMapsLoaded: this.googleMapsLoaded(), filtersModalOpen: false }
  }

  componentDidMount() {
    this.reinitOverflowShadow()
    this.checkGoogleMapsLoaded()
  }

  googleMapsLoaded() {
    return !!(window.google && window.google.maps)
  }

  checkGoogleMapsLoaded = () => {
    if (!this.state.googleMapsLoaded) {
      if (this.googleMapsLoaded()) {
        this.setState({ googleMapsLoaded: true })
      } else {
        setTimeout(this.checkGoogleMapsLoaded, 100)
      }
    }
  }

  _handleRef = el => {
    this._geosuggest = el
  }

  reinitOverflowShadow = () => window.initOverflowShadow && window.initOverflowShadow()

  openFiltersModal = () => {
    this.setState({ filtersModalOpen: true })
    // document.body.classList.add('blur-main')
  }
  closeFiltersModal = () => {
    this.setState({ filtersModalOpen: false })
    // document.body.classList.remove('blur-main')
  }

  render() {
    const {
      filters,
      toggleParamValue,
      setParams,
      clearParams,
      clearAllParams,
      experiences,
      categories,
      accessibilities,
      venueGroups,
      venues,
      days,
      sortby,
    } = this.props

    const { filtersModalOpen } = this.state
    const daysObj = days.reduce((obj, day) => ({ ...obj, [day]: moment(day).format('ddd D') }), {})

    // exlcude sortby from filtersAreActive
    const filtersAreActive = hasFilters({ ...filters, sortby: undefined })

    const firstDay = moment(days.sort()[0])
      .add(-7, 'days')
      .format('YYYY-MM-DD')
    const lastDay = days.sort()[days.length - 1]
    const today = moment().format('YYYY-MM-DD')
    const withinFestival = today > firstDay && today < lastDay

    return (
      <>
        <section className="search-wrapper" id="search-wrapper">
          <header>
            <form onSubmit={e => e.preventDefault()}>
              {/* Tab Content */}
              <div className="container">
                <div className="event-filter-bar flex align-items-center">
                  <div className="flex-s-up align-items-center flex-1-1-auto hide-xs">
                    <label className="no-margin flex-none">{filtersAreActive ? `You've selected` : 'Show me'}:</label>
                    <nav className="group flex-1-1-auto">
                      <FilterButton
                        active={!filtersAreActive}
                        onSelect={clearAllParams}
                        label={filtersAreActive ? 'Clear' : 'All'}
                      />
                      <FilterButton
                        active={filters.highlight}
                        onSelect={() => toggleParamValue('highlight', 1)}
                        label="Festival highlights"
                      />
                      <FilterButton
                        active={filters.free}
                        onSelect={() => toggleParamValue('free', 1)}
                        label="Fun &amp; Free"
                      />
                      {(filters.experience || []).length > 0 && (
                        <FilterButton
                          active
                          onSelect={() => setParams({ experience: null })}
                          data={experiences.filter(({ id }) => filters.experience.includes(id)).map(e => e.title)}
                          groupLabel="Experiences"
                        />
                      )}
                      {(filters.category || []).length > 0 && (
                        <FilterButton
                          active
                          onSelect={() => setParams({ category: null })}
                          data={categories.filter(({ id }) => filters.category.includes(id)).map(c => c.title)}
                          groupLabel="Genres"
                        />
                      )}
                      {((filters.region || []).length > 0 || (filters.venue || []).length > 0) && (
                        <FilterButton
                          active
                          onSelect={() => setParams({ region: null, venue: null })}
                          data={[
                            ...venueGroups.filter(({ id }) => filters.region.includes(id)).map(r => r.title),
                            ...discardVenuesInGroups(venues, venueGroups)
                              .filter(({ id }) => (filters.venue || []).includes(id))
                              .map(v => v.title),
                          ]}
                          groupLabel="Locations"
                        />
                      )}
                      {(filters.day || []).length > 0 ? (
                        <FilterButton
                          active
                          onSelect={() => setParams({ day: null })}
                          data={filters.day.map(d => moment(d).format('D'))}
                          mapper={data => data.join(', ') + ' Sep'}
                          groupLabel="Dates"
                        />
                      ) : (
                        <>
                          {withinFestival && (
                            <>
                              <FilterButton
                                active={filters.period === 'this-week'}
                                onSelect={() => toggleParamValue('period', 'this-week')}
                                label="This week"
                              />
                              <FilterButton
                                active={filters.period === 'this-weekend'}
                                onSelect={() => toggleParamValue('period', 'this-weekend')}
                                label="This weekend"
                              />
                            </>
                          )}
                          <FilterButton
                            active={filters.period === 'all-weekends'}
                            onSelect={() => toggleParamValue('period', 'all-weekends')}
                            label="All weekends"
                          />
                        </>
                      )}
                      {(filters.a11y || []).length > 0 && (
                        <FilterButton
                          active
                          onSelect={() => setParams({ a11y: null })}
                          groupLabel="Accessibilities"
                          className="search-button-a11y"
                          a11y
                          minGroup={4}
                          data={accessibilities.filter(({ id }) => filters.a11y.includes(id))}
                          mapper={data => (
                            <>
                              {data.map(a11y =>
                                a11y.icon ? (
                                  <img width={24} height={24} src={getImageUrl(a11y.icon, 'favicon-48')} />
                                ) : (
                                  a11y.short_title || a11y.title
                                )
                              )}
                            </>
                          )}
                        />
                      )}
                    </nav>
                  </div>
                  <div className="event-filter-bar-right flex-none">
                    <button
                      type="button"
                      className={cn(
                        'button button-xs button-pink filters-modal-button',
                        filtersAreActive && 'filters-active'
                      )}
                      onClick={() => this.openFiltersModal()}>
                      <span className="active-filter-count">
                        {countFilters(filters) !== 0 && countFilters(filters)}
                      </span>
                      <svg width="24" height="24">
                        <use xlinkHref="/assets/glyphs/glyphs.svg?cachebust=2024#filter"></use>
                      </svg>
                      <span>Filters</span>
                    </button>
                  </div>
                </div>
              </div>
              <hr className="no-margin border-color-concrete-10 hide-xs" />
              <div className="container">
                <div className="sub-filter-bar flex justify-content-space-between flex-wrap gap-xxs padding-top-m">
                  <div className="flex align-items-center justify-content-space-between-s-down width-s-down-1">
                    <label className="font-size-xl no-margin flex-none padding-right-m">Sort by:</label>
                    <div className="filter-sortby">
                      <select value={filters.sortby || undefined} onChange={e => setParams({ sortby: e.target.value })}>
                        <option value="recommended">Recommended</option>
                        <option value="date">Date</option>
                      </select>
                      <svg width="24" height="24">
                        <use xlinkHref="/assets/glyphs/glyphs.svg?cachebust=2024#chevron-down" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex align-items-center justify-content-space-between-s-down width-s-down-1">
                    <label className="font-size-xl no-margin flex-none padding-right-m">View as:</label>
                    <div className="filter-view">
                      <div className="button-group">
                        <button
                          type="button"
                          onClick={() => setParams({ view: 'catalogue' })}
                          className={cn('button-icon button-icon-xxxs search-button no-margin-right no-margin-bottom', {
                            'search-button-active': !filters.view || filters.view === 'catalogue',
                          })}>
                          <svg width="24" height="24" className="no-margin-right-s-down">
                            <use xlinkHref="/assets/glyphs/glyphs.svg?cachebust=2024#book" />
                          </svg>
                          <span className="hide-xs">Catalogue</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setParams({ view: 'schedule' })}
                          className={cn('button-icon button-icon-xxxs search-button no-margin-right no-margin-bottom', {
                            'search-button-active': filters.view === 'schedule',
                          })}>
                          <svg width="24" height="24" className="no-margin-right-s-down">
                            <use xlinkHref="/assets/glyphs/glyphs.svg?cachebust=2024#calendar" />
                          </svg>
                          <span className="hide-xs">Schedule</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </header>
        </section>
        <div className={cn('modal-container', filtersModalOpen && 'open')} onClick={() => this.closeFiltersModal()}>
          <aside className="modal filters-modal" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              className={cn('button-text button-icon button-square button-s color-primary close-button')}
              onClick={() => this.closeFiltersModal()}>
              <svg width="24" height="24">
                <use xlinkHref="/assets/glyphs/glyphs.svg?cachebust=2024#close-thin"></use>
              </svg>
            </button>
            <div className="filters-modal-inner">
              <div className="filters-modal-body">
                <div className="search-group">
                  <h2>Why not try?</h2>
                  <nav className="group">
                    <FilterButton
                      active={!(filters.experience || []).length}
                      onSelect={() => clearParams('experience')}
                      label="All"
                    />
                    {experiences.map(experience => (
                      <FilterButton
                        key={experience.id}
                        active={(filters.experience || []).includes(experience.id)}
                        onSelect={() => toggleParamValue('experience', experience.id)}
                        label={experience.title}
                      />
                    ))}
                  </nav>
                </div>
                <div className="search-group">
                  <h2>Genre</h2>
                  <nav className="group">
                    <FilterButton
                      active={!(filters.category || []).length}
                      onSelect={() => clearParams('category')}
                      label="All"
                    />
                    {categories.map(category => (
                      <FilterButton
                        key={category.id}
                        active={(filters.category || []).includes(category.id)}
                        onSelect={() => toggleParamValue('category', category.id)}
                        label={category.title}
                      />
                    ))}
                  </nav>
                </div>
                <div className="search-group">
                  <h2>Date</h2>
                  <nav className="group">
                    <FilterButton
                      active={!(filters.day || []).length && !filters.period}
                      onSelect={() => setParams({ period: null, day: null })}
                      label="All"
                    />
                    {withinFestival && (
                      <>
                        <FilterButton
                          active={filters.period === 'this-week'}
                          onSelect={() =>
                            setParams({ period: filters.period === 'this-week' ? null : 'this-week', day: [] })
                          }
                          label="This week"
                        />
                        <FilterButton
                          active={filters.period === 'this-weekend'}
                          onSelect={() =>
                            setParams({ period: filters.period === 'this-weekend' ? null : 'this-weekend', day: [] })
                          }
                          label="This weekend"
                        />
                      </>
                    )}
                    <FilterButton
                      active={filters.period === 'all-weekends'}
                      onSelect={() =>
                        setParams({ period: filters.period === 'all-weekends' ? null : 'all-weekends', day: [] })
                      }
                      label="All weekends"
                    />
                  </nav>
                  <nav className="date-group padding-bottom-m" data-overflow-shadow-inner>
                    {Object.keys(daysObj).map(key => {
                      const selected = (filters.day || []).indexOf(key) >= 0
                      const externallySelected = dateIsSelectedByPeriod(filters.period, key)
                      return (
                        <div
                          key={key}
                          className={cn(
                            'date-button',
                            selected && 'date-button-active',
                            externallySelected && 'date-button-semi-active'
                          )}
                          onClick={() =>
                            setParams({
                              period: null,
                              day: selected ? filters.day.filter(d => d !== key) : [...(filters.day || []), key],
                            })
                          }>
                          <div className="date-button-inner background-light padding-xs font-weight-light">
                            <div>{moment(key).format('ddd') || <>&nbsp;</>}</div>
                            <big>{moment(key).format('D')}</big>
                            <div>{transformShortMonth(moment(key).format('MMM'))}</div>
                          </div>
                        </div>
                      )
                    })}
                    <div className="flex-none space space-xs hide-m-up" />
                  </nav>
                </div>
                <div className={('search-group ', filters.venue.length && 'disabled opacity-50')}>
                  <h2>Location</h2>
                  <nav className="group">
                    <FilterButton
                      active={!(filters.region || []).length}
                      onSelect={() => clearParams(['region'])}
                      label="All"
                    />
                    {venueGroups.map(venueGroup => (
                      <FilterButton
                        key={venueGroup.id}
                        active={(filters.region || []).includes(venueGroup.id)}
                        onSelect={() => toggleParamValue('region', venueGroup.id)}
                        label={venueGroup.short_title || venueGroup.title}
                      />
                    ))}
                  </nav>
                </div>
                <div className={('search-group ', filters.region.length && 'disabled opacity-50')}>
                  <h2>Venue</h2>
                  <nav className="group">
                    <FilterButton
                      active={!(filters.venue || []).length}
                      onSelect={() => clearParams(['venue'])}
                      label="All"
                    />
                    {discardVenuesInGroups(venues, venueGroups).map(venue => (
                      <FilterButton
                        key={venue.id}
                        active={(filters.venue || []).includes(venue.id)}
                        onSelect={() => toggleParamValue('venue', venue.id)}
                        label={venue.short_title || venue.title}
                      />
                    ))}
                  </nav>
                </div>
                <div className="search-group">
                  <h2>Accessibility</h2>
                  <div className="search-group">
                    {/* <div className="space space-s" /> */}
                    <nav className="flex flex-row flex-wrap justify-content-flex-start">
                      {accessibilities.map(accessibility => {
                        const active = (filters.a11y || []).indexOf(accessibility.id) >= 0
                        return (
                          <label
                            key={accessibility.id}
                            className="control control-inline checkbox"
                            style={{ flex: '0 0 250px', maxWidth: 250, margin: '0.5em 0', minWidth: '50%' }}>
                            <input
                              type="checkbox"
                              checked={active}
                              onClick={() => toggleParamValue('a11y', accessibility.id)}
                              onChange={() => {}}
                            />
                            <span className="control-indicator"></span>
                            <span className="control-label font-size-m font-weight-regular font-family-sans-serif line-height-l">
                              {accessibility.icon && (
                                <img
                                  width={24}
                                  height={24}
                                  style={{ verticalAlign: 'middle', marginRight: 5 }}
                                  src={getImageUrl(accessibility.icon, 'favicon-48')}
                                />
                              )}
                              {accessibility.short_title || accessibility.title}
                            </span>
                          </label>
                        )
                      })}
                      {[...Array(4)].map((_, i) => (
                        <div key={i} style={{ flex: '0 0 240px', minWidth: 240, marginRight: '2rem' }} />
                      ))}
                    </nav>
                    {/* <div className="space space-xs" /> */}
                  </div>
                </div>
              </div>
              <div className="filters-modal-footer padding-s" onClick={() => this.closeFiltersModal()}>
                <div className="filters-modal-footer-inner flex justify-content-space-between">
                  <button
                    type="button"
                    className="button button-s color-primary button-text"
                    onClick={() => clearAllParams()}>
                    Clear filters
                  </button>
                  <button
                    type="button"
                    className="button button-s button-pink"
                    onClick={() => this.closeFiltersModal()}>
                    SEARCH
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </>
    )
  }
}
